import { ComponentProvider } from "@inductiveautomation/ws-components";
import { useTranslation } from "react-i18next";

const CustomComponentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en-US";

  return (
    <ComponentProvider
      locale={locale}
      baseAssetUrl={window._acctConfig?.BASE_ASSET_URL ?? ""}
      environment={window._acctConfig?.ENVIRONMENT ?? "production"}
      translations={{
        common: {
          actions: t("common.actions"),
          code_block: {
            label: t("common.code_block.label"),
            copy_message: t("common.code_block.copy_message"),
            code: t("common.code_block.code"),
          },
          checkbox_tooltip: t("common.checkbox_tooltip"),
          radio_tooltip: t("common.radio_tooltip"),
          switch_tooltip: t("common.switch_tooltip"),
          select_date: t("common.select_date"),
          delete: t("common.delete"),
          close: t("common.close"),
          clear: t("common.clear"),
          filter: t("common.filter"),
          all: t("common.all"),
          search: t("common.search"),
          select_all: t("common.select_all"),
          clear_all: t("common.clear_all"),
          clear_all_filters: t("common.clear_all_filters"),
          no_results: t("common.no_results"),
          no_results_match_your_search: t(
            "common.no_results_match_your_search"
          ),
          no_data_available_to_filter: t(
            "common.no_data_available_to_filter"
          ),
          cancel: t("common.cancel"),
          edit: t("common.edit"),
          save: t("common.save"),
          back: t("common.back"),
          next: t("common.next"),
          start: t("common.start"),
          finish: t("common.finish"),
          skip_step: t("common.skip_step"),
          print_receipt: t("common.print_receipt"),
          form: {
            error_message: t("common.form.error_message"),
            exceeds_max_length_error: t("common.form.exceeds_max_length_error"),
            required_error: t("common.form.required_error"),
            success_message: t("common.form.success_message"),
          },
          inputs: {
            optional: "Optional",
            phone: {
              label: t("common.inputs.phone.label"),
              non_numeric_error: t("common.inputs.phone.non_numeric_error"),
              not_possible_error: t("common.inputs.phone.not_possible_error"),
            },
          },
          date: t("common.date"),
          invalid_date: t("common.invalid_date"),
          save_and_review: t("common.save_and_review"),
          copy: t("common.copy.label"),
          copy_message: t("common.copy.message"),
          select_a_step: t("common.select_a_step"),
          show: t("common.show"),
          hide: t("common.hide"),
          show_contents: t("common.show_contents"),
          hide_contents: t("common.hide_contents"),
          download: t("common.download"),
          done: t("common.done"),
        },
        errors: {
          request_failed: t("common.errors.request_failed"),
          general_message: t("common.errors.general_message"),
          reload_page: t("common.errors.reload_page"),
          help_center: t("common.errors.help_center"),
        },
        file_upload: {
          drop_files: t("file_upload.drop_files"),
          uploading: t("file_upload.uploading"),
          retry: t("file_upload.retry"),
          error: t("file_upload.error"),
          choose_files: t("file_upload.choose_files"),
          drag_files: t("file_upload.drag_files"),
          view: t("file_upload.view"),
          download: t("file_upload.download"),
          choose_file: t("file_upload.choose_file"),
          choose_new_file: t("file_upload.choose_new_file"),
          replace_file: t("file_upload.replace_file"),
          drop_file: t("file_upload.drop_file"),
          drag_file: t("file_upload.drag_file"),
        },
        date_picker: {
          previous_month: t("common.date_picker.previous_month"),
          select_month: t("common.date_picker.select_month"),
          next_month: t("common.date_picker.next_month"),
          previous_year: t("common.date_picker.previous_year"),
          select_year: t("common.date_picker.select_year"),
          next_year: t("common.date_picker.next_year"),
        },
        unsaved_warning: {
          heading: t("common.unsaved_warning.heading"),
          description: t("common.unsaved_warning.description"),
          dialog_description: t("common.unsaved_warning.dialog_description"),
          cancel_label: t("common.unsaved_warning.cancel_label"),
          submit_label: t("common.unsaved_warning.submit_label"),
        },
        restricted_access: {
          heading: t("common.restricted_access.heading"),
          description: t("common.restricted_access.description"),
        },
        avatar_upload: {
          uploaded_avatar_alt_text: t(
            "common.avatar_upload.uploaded_avatar_alt_text"
          ),
          preview_avatar_alt_text: t(
            "common.avatar_upload.preview_avatar_alt_text"
          ),
          remove_avatar_description: t(
            "common.avatar_upload.remove_avatar_description"
          ),
          actions: {
            edit_button_label: t(
              "common.avatar_upload.actions.edit_button_label"
            ),
            cancel: t("common.avatar_upload.actions.cancel"),
            try_again: t("common.avatar_upload.actions.try_again"),
            crop_photo: t("common.avatar_upload.actions.crop_photo"),
            back: t("common.avatar_upload.actions.back"),
            done: t("common.avatar_upload.actions.done"),
            change: t("common.avatar_upload.actions.change"),
            remove: t("common.avatar_upload.actions.remove"),
            remove_profile_picture: t(
              "common.avatar_upload.actions.remove_profile_picture"
            ),
          },
          headings: {
            uploading: t("common.avatar_upload.headings.uploading"),
            error: t("common.avatar_upload.headings.error"),
            crop: t("common.avatar_upload.headings.crop"),
            preview: t("common.avatar_upload.headings.preview"),
            edit_existing: t("common.avatar_upload.headings.edit_existing"),
            remove_existing: t("common.avatar_upload.headings.remove_existing"),
          },
          errors: {
            invalid_file_type: t(
              "common.avatar_upload.errors.invalid_file_type"
            ),
            file_size_limit: t("common.avatar_upload.errors.file_size_limit"),
            megabyte_abbreviation: t(
              "common.avatar_upload.errors.megabyte_abbreviation"
            ),
            uploaded_file_size: t(
              "common.avatar_upload.errors.uploaded_file_size"
            ),
            bytes: t("common.avatar_upload.errors.bytes"),
            no_file_selected: t("common.avatar_upload.errors.no_file_selected"),
          },
        },
        item_drawer: {
          meta_heading: t("common.item_drawer.meta_heading"),
          tag_heading: t("common.item_drawer.tag_heading"),
          tag_helper_message: t("common.item_drawer.tag_helper_message"),
          tag_validation_errors: {
            invalid_tag: t(
              "common.item_drawer.tag_validation_errors.invalid_tag"
            ),
            tag_already_in_use: t(
              "common.item_drawer.tag_validation_errors.tag_already_in_use"
            ),
          },
          tag_add_button_label: t("common.item_drawer.tag_add_button_label"),
          info_tab_label: t("common.item_drawer.info_tab_label"),
          activity_tab_label: t("common.item_drawer.activity_tab_label"),
          pinned_heading: t("common.item_drawer.pinned_heading"),
          all_heading: t("common.item_drawer.all_heading"),
          no_activity: t("common.item_drawer.no_activity"),
          no_activity_for_item: t("common.item_drawer.no_activity_for_item"),
          no_results: t("common.item_drawer.no_results"),
          no_results_match_search: t(
            "common.item_drawer.no_results_match_search"
          ),
          activity_event_filter: t("common.item_drawer.event_filter"),
          activity_people_filter: t("common.item_drawer.people_filter"),
          add_note: t("common.item_drawer.add_note"),
          activity_items: {
            add_note: t("common.item_drawer.activity_items.add_note"),
            edit_note: t("common.item_drawer.activity_items.edit_note"),
            pin_note: t("common.item_drawer.activity_items.pin_note"),
            unpin_note: t("common.item_drawer.activity_items.unpin_note"),
            pin_event: t("common.item_drawer.activity_items.pin_event"),
            unpin_event: t("common.item_drawer.activity_items.unpin_event"),
            delete_note: t("common.item_drawer.activity_items.delete_note"),
          },
        },
        data_grid: {
          table_options: t("common.data_grid.table_options"),
          sort_drawer_heading: t("common.data_grid.sort_drawer_heading"),
          download_dialog: {
            headline: t("common.data_grid.download_dialog.headline"),
            cancel_label: t("common.data_grid.download_dialog.cancel_label"),
            content_area_text: (count: number, item_label: string) =>
              t("common.data_grid.download_dialog.content_area_text", {
                itemsWithCount: `${count} ${item_label}`,
              }),
            download_submit_label: t(
              "common.data_grid.download_dialog.download_submit_label"
            ),
          },
          selected_count_suffix: t("common.data_grid.selected_count_suffix"),
          filter_tooltip: t("common.data_grid.filter_tooltip"),
          download_tooltip: t("common.data_grid.download_tooltip"),
          download_tooltip_disabled: t("common.data_grid.download_tooltip_disabled"),
          info_tooltip: t("common.data_grid.info_tooltip"),
          clear_selection_tooltip: t(
            "common.data_grid.clear_selection_tooltip"
          ),
        },
        forms: {
          prefix: t("common.forms.prefix"),
          street_address_line1: t("common.forms.street_address_line1"),
          street_address_line2: t("common.forms.street_address_line2"),
          suggestions: t("common.forms.suggestions"),
          show_street_address_line2: t(
            "common.forms.show_street_address_line2"
          ),
          hide_street_address_line2: t(
            "common.forms.hide_street_address_line2"
          ),
        },
      }}
    >
      {children}
    </ComponentProvider>
  );
};

export default CustomComponentProvider;
